import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getCategory, createCategory, updateCategory } from '../../../services/categoryService';
import { uploadImage } from '../../../services/uploadService';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiUpload, FiChevronDown, FiChevronUp, FiTrash2, FiCheck, FiImage } from 'react-icons/fi';
import Papa from 'papaparse';

const CategoriesFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        imageUrl: '',
        isActive: true,
        order: 0
    });

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Bulk upload states
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [bulkCategories, setBulkCategories] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [submittingIndex, setSubmittingIndex] = useState(null);
    const [isSubmittingAll, setIsSubmittingAll] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            fetchCategory();
        }
    }, [id]);

    const fetchCategory = async () => {
        try {
            setLoading(true);
            const response = await getCategory(id);
            const category = response.data;

            setFormData({
                name: category.name || '',
                description: category.description || '',
                imageUrl: category.imageUrl || '',
                isActive: category.isActive !== undefined ? category.isActive : true,
                order: category.order || 0
            });
        } catch (error) {
            console.error('Error fetching category:', error);
            toast.error('Failed to load category data');
            navigate('/admin/categories');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size must be less than 5MB');
                return;
            }

            setImageFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageUpload = async () => {
        if (!imageFile) {
            toast.error('Please select an image first');
            return;
        }

        try {
            setUploadingImage(true);
            const token = localStorage.getItem('token');
            const response = await uploadImage(imageFile, token);
            
            setFormData(prev => ({
                ...prev,
                imageUrl: response.data.imageUrl
            }));
            
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setFormData(prev => ({
            ...prev,
            imageUrl: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            setSubmitting(true);

            if (isEditMode) {
                await updateCategory(id, formData);
                toast.success(`Category "${formData.name}" updated successfully`);
            } else {
                await createCategory(formData);
                toast.success(`Category "${formData.name}" created successfully`);
            }

            navigate('/admin/categories');
        } catch (error) {
            console.error('Error saving category:', error);
            toast.error(error.response?.data?.message || 'Failed to save category');
        } finally {
            setSubmitting(false);
        }
    };

    // Bulk upload functions
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                // Map the parsed data to our format
                const parsedCategories = results.data.map((item, index) => ({
                    id: `temp-${index}`,
                    name: item.name || '',
                    description: item.description || '',
                    imageUrl: item.imageUrl || '',
                    order: parseInt(item.order) || 0,
                    isActive: item.isActive ? item.isActive.toLowerCase() === 'true' : true,
                    status: 'pending' // pending, success, error
                }));

                setBulkCategories(parsedCategories);
                setIsUploading(false);
                toast.success(`Successfully parsed ${parsedCategories.length} categories`);
            },
            error: (error) => {
                console.error('CSV Parse Error:', error);
                toast.error('Error parsing CSV file');
                setIsUploading(false);
            }
        });

        // Reset the file input
        e.target.value = null;
    };

    const handleBulkInputChange = (index, field, value) => {
        const updatedCategories = [...bulkCategories];
        if (field === 'isActive') {
            updatedCategories[index][field] = value;
        } else if (field === 'order') {
            updatedCategories[index][field] = parseInt(value) || 0;
        } else {
            updatedCategories[index][field] = value;
        }
        setBulkCategories(updatedCategories);
    };

    const handleRemoveRow = (index) => {
        const updatedCategories = [...bulkCategories];
        updatedCategories.splice(index, 1);
        setBulkCategories(updatedCategories);
    };

    const handleAddEmptyRow = () => {
        setBulkCategories([
            ...bulkCategories,
            {
                id: `temp-${Date.now()}`,
                name: '',
                description: '',
                imageUrl: '',
                order: 0,
                isActive: true,
                status: 'pending'
            }
        ]);
    };

    const validateBulkCategory = (category) => {
        if (!category.name.trim()) {
            return { valid: false, message: 'Name is required' };
        }
        if (!category.description.trim()) {
            return { valid: false, message: 'Description is required' };
        }
        return { valid: true };
    };

    const handleSubmitRow = async (index) => {
        const category = bulkCategories[index];
        const validation = validateBulkCategory(category);

        if (!validation.valid) {
            toast.error(`Row ${index + 1}: ${validation.message}`);
            return;
        }

        setSubmittingIndex(index);

        try {
            const updatedCategories = [...bulkCategories];
            updatedCategories[index].status = 'submitting';
            setBulkCategories(updatedCategories);

            let imageUrl = '';
            
            // Upload image if provided
            if (category.imageFile) {
                const token = localStorage.getItem('token');
                const uploadResponse = await uploadImage(category.imageFile, token);
                imageUrl = uploadResponse.data.imageUrl;
            }

            await createCategory({
                name: category.name,
                description: category.description,
                imageUrl: imageUrl,
                order: category.order,
                isActive: category.isActive
            });

            updatedCategories[index].status = 'success';
            setBulkCategories(updatedCategories);
            toast.success(`Category "${category.name}" created successfully`);
        } catch (error) {
            console.error('Error creating category:', error);
            const updatedCategories = [...bulkCategories];
            updatedCategories[index].status = 'error';
            updatedCategories[index].errorMessage = error.response?.data?.message || 'Failed to create category';
            setBulkCategories(updatedCategories);
            toast.error(`Failed to create category "${category.name}": ${updatedCategories[index].errorMessage}`);
        } finally {
            setSubmittingIndex(null);
        }
    };

    const handleSubmitAll = async () => {
        if (bulkCategories.length === 0) {
            toast.warning('No categories to submit');
            return;
        }

        // Validate all categories first
        for (let i = 0; i < bulkCategories.length; i++) {
            const category = bulkCategories[i];
            if (category.status === 'success') continue; // Skip already successful ones

            const validation = validateBulkCategory(category);
            if (!validation.valid) {
                toast.error(`Row ${i + 1}: ${validation.message}`);
                return;
            }
        }

        setIsSubmittingAll(true);

        try {
            const updatedCategories = [...bulkCategories];
            let successCount = 0;
            let errorCount = 0;

            for (let i = 0; i < updatedCategories.length; i++) {
                const category = updatedCategories[i];

                // Skip already processed categories
                if (category.status === 'success') {
                    successCount++;
                    continue;
                }

                try {
                    setSubmittingIndex(i);

                    // Update status to submitting
                    updatedCategories[i].status = 'submitting';
                    setBulkCategories([...updatedCategories]);

                    let imageUrl = '';
                    
                    // Upload image if provided
                    if (category.imageFile) {
                        const token = localStorage.getItem('token');
                        const uploadResponse = await uploadImage(category.imageFile, token);
                        imageUrl = uploadResponse.data.imageUrl;
                    }

                    await createCategory({
                        name: category.name,
                        description: category.description,
                        imageUrl: imageUrl,
                        order: category.order,
                        isActive: category.isActive
                    });

                    updatedCategories[i].status = 'success';
                    successCount++;
                } catch (error) {
                    console.error('Error creating category:', error);
                    updatedCategories[i].status = 'error';
                    updatedCategories[i].errorMessage = error.response?.data?.message || 'Failed to create category';
                    errorCount++;
                }

                // Update the state after each operation
                setBulkCategories([...updatedCategories]);
            }

            if (successCount > 0) {
                toast.success(`Successfully created ${successCount} categories`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to create ${errorCount} categories`);
            }
        } finally {
            setIsSubmittingAll(false);
            setSubmittingIndex(null);
        }
    };

    const downloadSampleCSV = () => {
        const headers = ['name', 'description', 'order', 'isActive'];
        const sampleData = [
            {
                name: 'Skincare',
                description: 'Products for face and skin',
                order: 1,
                isActive: 'true'
            },
            {
                name: 'Haircare',
                description: 'Products for hair',
                order: 2,
                isActive: 'true'
            }
        ];

        const csvContent = Papa.unparse({
            fields: headers,
            data: sampleData
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'category_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearBulkData = () => {
        if (window.confirm('Are you sure you want to clear all bulk data?')) {
            setBulkCategories([]);
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {isEditMode ? 'Edit Category' : 'Create Category'}
                </h1>

                {!isEditMode && (
                    <button
                        type="button"
                        className="btn btn-outline flex items-center"
                        onClick={() => setShowBulkUpload(!showBulkUpload)}
                    >
                        {showBulkUpload ? <FiChevronUp className="mr-2" /> : <FiChevronDown className="mr-2" />}
                        {showBulkUpload ? 'Hide Bulk Upload' : 'Show Bulk Upload'}
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    {/* Single Category Form */}
                    {(!showBulkUpload || isEditMode) && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category Name*
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description*
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="input min-h-[100px]"
                                            required
                                        ></textarea>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category Image
                                        </label>
                                        
                                        {/* Image Upload Section */}
                                        <div className="space-y-3">
                                            {/* File Input */}
                                            <div className="flex items-center space-x-3">
                                                <label className="flex items-center px-4 py-2 bg-gray-100 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                                                    <FiImage className="w-4 h-4 mr-2" />
                                                    Choose Image
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                        className="hidden"
                                                    />
                                                </label>
                                                
                                                {imageFile && (
                                                    <button
                                                        type="button"
                                                        onClick={handleImageUpload}
                                                        disabled={uploadingImage}
                                                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        <FiUpload className="w-4 h-4 mr-2" />
                                                        {uploadingImage ? 'Uploading...' : 'Upload'}
                                                    </button>
                                                )}
                                                
                                                {(imageFile || formData.imageUrl) && (
                                                    <button
                                                        type="button"
                                                        onClick={removeImage}
                                                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                                    >
                                                        <FiX className="w-4 h-4 mr-2" />
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {/* Image Preview */}
                                            {(imagePreview || formData.imageUrl) && (
                                                <div className="mt-3">
                                                    <img
                                                        src={imagePreview || formData.imageUrl}
                                                        alt="Preview"
                                                        className="h-32 w-32 object-cover rounded-md border border-gray-200"
                                                    />
                                                </div>
                                            )}
                                            
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Display Order
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleChange}
                                            className="input"
                                            min="0"
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleChange}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/categories')}
                                        className="btn btn-outline flex items-center"
                                    >
                                        <FiX className="mr-2" /> Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`btn btn-primary flex items-center ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FiSave className="mr-2" /> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Bulk Upload Section */}
                    {showBulkUpload && !isEditMode && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Bulk Create Categories</h2>
                                <div className="mb-6">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Upload a CSV file with category data or add entries manually. The CSV should include columns for name, description, order, and isActive. Images can be uploaded individually for each category.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <label className="btn btn-primary flex items-center cursor-pointer">
                                            <FiUpload className="mr-2" />
                                            Upload CSV
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                disabled={isUploading}
                                            />
                                        </label>

                                        <button
                                            onClick={downloadSampleCSV}
                                            className="btn btn-outline flex items-center text-sm"
                                        >
                                            <FiUpload className="mr-1" /> Download Template
                                        </button>

                                        <button
                                            onClick={handleAddEmptyRow}
                                            className="btn btn-outline flex items-center"
                                        >
                                            <FiUpload className="mr-2" /> Add Row
                                        </button>

                                        {bulkCategories.length > 0 && (
                                            <>
                                                <button
                                                    onClick={clearBulkData}
                                                    className="btn btn-outline flex items-center text-red-600 border-red-600 hover:bg-red-50"
                                                >
                                                    <FiTrash2 className="mr-2" /> Clear All
                                                </button>

                                                <button
                                                    onClick={handleSubmitAll}
                                                    disabled={isSubmittingAll || bulkCategories.length === 0}
                                                    className={`btn btn-primary flex items-center ml-auto ${isSubmittingAll ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                >
                                                    {isSubmittingAll ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                            Saving All...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiSave className="mr-2" /> Save All
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {isUploading ? (
                                    <div className="text-center py-10">
                                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mb-4"></div>
                                        <p>Parsing CSV...</p>
                                    </div>
                                ) : bulkCategories.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Description*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Image
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Order
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Active
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                                {bulkCategories.map((category, index) => (
                                                    <tr key={category.id} className={`${category.status === 'error' ? 'bg-red-50' : category.status === 'success' ? 'bg-green-50' : ''}`}>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={category.name}
                                                                onChange={(e) => handleBulkInputChange(index, 'name', e.target.value)}
                                                                disabled={category.status === 'success' || category.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm"
                                                                placeholder="Category name"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={category.description}
                                                                onChange={(e) => handleBulkInputChange(index, 'description', e.target.value)}
                                                                disabled={category.status === 'success' || category.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm"
                                                                placeholder="Description"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex items-center space-x-2">
                                                                <label className="flex items-center px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs cursor-pointer hover:bg-gray-200 transition-colors">
                                                                    <FiImage className="w-3 h-3 mr-1" />
                                                                    Upload
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0];
                                                                            if (file) {
                                                                                // Validate file type
                                                                                if (!file.type.startsWith('image/')) {
                                                                                    toast.error('Please select an image file');
                                                                                    return;
                                                                                }
                                                                                // Validate file size (5MB limit)
                                                                                if (file.size > 5 * 1024 * 1024) {
                                                                                    toast.error('Image size must be less than 5MB');
                                                                                    return;
                                                                                }
                                                                                // Store file for upload
                                                                                const updatedCategories = [...bulkCategories];
                                                                                updatedCategories[index].imageFile = file;
                                                                                setBulkCategories(updatedCategories);
                                                                            }
                                                                        }}
                                                                        className="hidden"
                                                                        disabled={category.status === 'success' || category.status === 'submitting' || submittingIndex === index}
                                                                    />
                                                                </label>
                                                                {category.imageFile && (
                                                                    <span className="text-xs text-green-600">
                                                                        {category.imageFile.name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="number"
                                                                value={category.order}
                                                                onChange={(e) => handleBulkInputChange(index, 'order', e.target.value)}
                                                                disabled={category.status === 'success' || category.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm w-16"
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={category.isActive}
                                                                onChange={(e) => handleBulkInputChange(index, 'isActive', e.target.checked)}
                                                                disabled={category.status === 'success' || category.status === 'submitting' || submittingIndex === index}
                                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {category.status === 'submitting' || submittingIndex === index ? (
                                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium">
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-primary-600 mr-1"></div>
                                                                    Submitting...
                                                                </span>
                                                            ) : category.status === 'success' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <FiCheck className="mr-1" />
                                                                    Success
                                                                </span>
                                                            ) : category.status === 'error' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={category.errorMessage}>
                                                                    <FiX className="mr-1" />
                                                                    Error
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex space-x-2">
                                                                {category.status !== 'success' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleSubmitRow(index)}
                                                                            disabled={category.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-primary-600 hover:text-primary-900 p-1"
                                                                            title="Save this category"
                                                                        >
                                                                            <FiSave size={16} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveRow(index)}
                                                                            disabled={category.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-red-600 hover:text-red-900 p-1"
                                                                            title="Remove this row"
                                                                        >
                                                                            <FiTrash2 size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No categories added yet. Upload a CSV or add rows manually.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </AdminLayout>
    );
};

export default CategoriesFormPage;